import { useQuery } from "@tanstack/react-query";

import { logger } from "@/lib/logger";
import {
  getToken,
  TokenCommand,
  tryGetTokenSilently,
} from "@/services/microsoft/auth";
import {
  searchSharePointSites,
  SITES_READ_ALL_SCOPE,
} from "@/services/microsoft/sharePointSitesService";

const GRAPH_RESOURCE = "https://graph.microsoft.com";
const SITES_QUERY_KEY = ["microsoft", "sites"] as const;
const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

const sitesTokenCommand: TokenCommand = {
  type: "Graph",
  resource: GRAPH_RESOURCE,
  scopes: [SITES_READ_ALL_SCOPE],
};

export const SITES_ACCESS_QUERY_KEY = [...SITES_QUERY_KEY, "access"] as const;

/**
 * Whether the Graph scope `Sites.Read.All` is already consented for this user.
 * Probes MSAL silently (never a popup, so it is safe to run without a user
 * gesture) and caches the answer for half an hour; a negative answer is
 * re-checked only after {@link requestSharePointSitesAccess} succeeds.
 */
export const useSharePointSitesAccess = (enabled: boolean) =>
  useQuery<boolean>({
    queryKey: SITES_ACCESS_QUERY_KEY,
    queryFn: async () =>
      (await tryGetTokenSilently(sitesTokenCommand)) !== null,
    enabled,
    retry: false,
    staleTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
  });

/**
 * Interactive consent for `Sites.Read.All`. Must be called from a user click
 * (it opens the Microsoft popup). Returns true when a token was obtained.
 */
export const requestSharePointSitesAccess = async (): Promise<boolean> => {
  try {
    await getToken(sitesTokenCommand);
    return true;
  } catch (error) {
    // Expected while the customer tenant requires admin consent (or the user
    // dismissed the popup); the dialog keeps its paste-a-link fallback.
    logger.warn("SharePoint site search access not granted", {
      operation: "sharePointSitesAccess",
      error,
    });
    return false;
  }
};

/** Graph site search; runs once access is confirmed and the query has two or more characters. */
export const useSharePointSiteSearch = (query: string, hasAccess: boolean) => {
  const normalized = query.trim().toLowerCase();
  return useQuery({
    queryKey: [...SITES_QUERY_KEY, "search", normalized],
    queryFn: async () => {
      const token = await tryGetTokenSilently(sitesTokenCommand);
      if (!token) throw new Error("Sites.Read.All token unavailable");
      return searchSharePointSites(normalized, token);
    },
    enabled: hasAccess && normalized.length >= 2,
    retry: false,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
  });
};
